import { Loader2, Send } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ApiError } from '@/core/api';
import { useMyMemberPublicId } from '@/stores/session';
import { useCreateInvitation } from '../queries';

/**
 * A pupil inviting their own parent (PRD §4.3 matrix, backend ADR-009:
 * `_validate_inviter_rights` allows `invited_by.is_pupil and pupil ==
 * invited_by`). The backend has permitted this since Phase 1 and nothing in
 * the UI ever offered it.
 *
 * The `pupil` field is the caller's OWN member public_id — the one
 * self-identification a pupil can read (`/users/current-user/`, ADR-016). It
 * is never a picker: a pupil may invite a parent for themselves and for nobody
 * else, and rendering a choice the server would refuse teaches them to try.
 *
 * The copy is written for a 14-year-old, not for a school administrator.
 */
export function InviteMyParentCard() {
  const { t } = useTranslation(['invitations']);
  const myMemberId = useMyMemberPublicId();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const createInvitation = useCreateInvitation();

  // Until the bootstrap lands there is no id to send, and a form that 400s on
  // submit is worse than one that is not there yet.
  if (!myMemberId) return null;

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const address = email.trim();
    if (!address) {
      setError(t('invitations:myParent.emailRequired'));
      return;
    }
    setError(null);
    try {
      await createInvitation.mutateAsync({
        email: address,
        role: 'parent',
        pupil: myMemberId as string,
      });
      setEmail('');
      setSentTo(address);
      toast.success(t('invitations:myParent.success'));
    } catch (e) {
      const err = e as ApiError;
      setError(err?.fieldErrors?.email?.join(' ') ?? err?.message ?? t('invitations:dialog.failed'));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('invitations:myParent.title')}</CardTitle>
        <CardDescription>{t('invitations:myParent.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <form onSubmit={e => void onSubmit(e)} className="flex flex-wrap items-end gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="invite-my-parent-email">{t('invitations:myParent.emailLabel')}</Label>
            <Input
              id="invite-my-parent-email"
              type="email"
              autoComplete="off"
              className="w-64"
              placeholder={t('invitations:dialog.emailPlaceholder')}
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                setSentTo(null);
              }}
            />
          </div>
          <Button type="submit" disabled={createInvitation.isPending}>
            {createInvitation.isPending ? <Loader2 className="animate-spin" /> : <Send />}
            {t('invitations:myParent.submit')}
          </Button>
        </form>
        {error ? <p className="text-destructive text-sm">{error}</p> : null}
        {sentTo && !error ? (
          <p className="text-muted-foreground text-sm">
            {t('invitations:myParent.sent', { email: sentTo })}
          </p>
        ) : null}
        <p className="text-muted-foreground text-sm">{t('invitations:myParent.note')}</p>
      </CardContent>
    </Card>
  );
}
